import React, { useState, useContext, useEffect, useRef } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import { useNavigate, Link } from "react-router-dom";
import { Row, Col} from "antd";
import { Card, Form, Input, Button, message, Typography, Divider } from "antd";
import { UserOutlined, LockOutlined, MailOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import OAuth from "./OAuth";
import UserContext from "../../contexts/UserContext";

export default function AdminRegister() {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    userRole: "USER",
    profileCompleted: false,
  });
  const { fullName, email, password, userRole } = formData;
  const { dispatch } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isMounted = useRef(true);

  const { Title, Text } = Typography;

  useEffect(() => {
    if (isMounted) {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          navigate("/");
        }
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, [navigate, isMounted]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async () => {
    setLoading(true);
    try {
      const userCredentials = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredentials.user;
      updateProfile(auth.currentUser, { displayName: fullName });
      const formDataCopy = { ...formData };
      delete formDataCopy.password;
      formDataCopy.timestamp = serverTimestamp();
      formDataCopy.uid = user.uid;
      formDataCopy.userRole = userRole;
      dispatch({ type: "SET_USER", payload: auth.currentUser.displayName });
      await setDoc(doc(db, "users", user.uid), formDataCopy);
      sendEmailVerification(auth.currentUser);
      auth.signOut();
      message.success("Please check your email and verify your account before logging in");
      navigate("/login");
    } catch (error) {
      form.resetFields();
      message.error(error.code);
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '120px 20px 80px', // Top padding for header, bottom for footer
      position: 'relative'
    }}>
      {/* Background decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '10%',
        width: '200px',
        height: '200px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '50%',
        animation: 'float 6s ease-in-out infinite'
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '20%',
        left: '5%',
        width: '150px',
        height: '150px',
        background: 'rgba(255, 215, 0, 0.2)',
        borderRadius: '50%',
        animation: 'float 4s ease-in-out infinite reverse'
      }}></div>

      {/* Back to Home Button */}
      <Link 
        to="/" 
        style={{
          position: 'absolute',
          top: '100px', // Moved down to avoid header overlap
          left: '30px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'white',
          textDecoration: 'none',
          padding: '10px 16px',
          borderRadius: '25px',
          background: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
        }}
      >
        <ArrowLeftOutlined />
        <span>Back to Home</span>
      </Link>

      <div style={{ 
        width: '100%', 
        maxWidth: '1200px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Row justify="center" align="middle" style={{ width: '100%' }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <Card
              style={{
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                border: 'none',
                background: 'white',
                margin: '0 auto'
              }}
              bodyStyle={{ padding: '50px 40px' }}
            >
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <Title 
                  level={2} 
                  style={{ 
                    color: '#1f2937', 
                    marginBottom: '8px',
                    fontSize: '2rem',
                    fontWeight: 'bold'
                  }}
                >
                  Join OkBrahmin
                </Title>
                <Text style={{ 
                  color: '#6b7280', 
                  fontSize: '16px' 
                }}>
                  Create your account to find your perfect match
                </Text>
              </div>

              {/* OAuth Component */}
              <div style={{ marginBottom: '30px' }}>
                <OAuth />
              </div>

              {/* Divider */}
              <Divider style={{ margin: '30px 0' }}>
                <span style={{ color: '#9ca3af', fontSize: '14px' }}>Or register with email</span>
              </Divider>

              {/* Registration Form */}
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSignup}
                autoComplete="off"
                requiredMark={false}
              >
                <Form.Item
                  name="fullName"
                  label={<span style={{ fontWeight: '600', color: '#374151' }}>Full Name</span>}
                  rules={[
                    {
                      required: true,
                      message: "Please enter your full name",
                    },
                    {
                      min: 2,
                      message: "Name must be at least 2 characters",
                    },
                    {
                      pattern: new RegExp(/^[a-zA-Z\s]+$/),
                      message: "Name can only contain letters and spaces",
                    }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Enter your full name"
                    size="large"
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px'
                    }}
                    name="fullName"
                    onChange={handleChange}
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label={<span style={{ fontWeight: '600', color: '#374151' }}>Email Address</span>}
                  rules={[
                    {
                      required: true,
                      message: "Please enter your email address",
                    },
                    {
                      type: 'email',
                      message: "Please enter a valid email address",
                    }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Enter your email address"
                    size="large"
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px'
                    }}
                    name="email"
                    onChange={handleChange}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<span style={{ fontWeight: '600', color: '#374151' }}>Password</span>}
                  rules={[
                    {
                      required: true,
                      message: "Please enter your password",
                    },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters long",
                    },
                    {
                      pattern: new RegExp(
                        "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$"
                      ),
                      message:
                        "Password must contain uppercase, lowercase, number, and special character",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Create a strong password"
                    size="large"
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px'
                    }}
                    name="password"
                    onChange={handleChange}
                  />
                </Form.Item>

                {/* Password Requirements */}
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '30px'
                }}>
                  <Text style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                    Password Requirements:
                  </Text>
                  <ul style={{
                    margin: '8px 0 0 0',
                    paddingLeft: '20px',
                    fontSize: '12px',
                    color: '#64748b',
                    lineHeight: '1.5'
                  }}>
                    <li>At least 8 characters long</li>
                    <li>One uppercase and lowercase letter</li>
                    <li>One number and special character</li>
                  </ul>
                </div>

                {/* Register Button */}
                <Form.Item style={{ marginBottom: '30px' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    style={{
                      width: '100%',
                      height: '50px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                    }}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </Form.Item>

                {/* Terms and Privacy */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: '30px',
                  padding: '16px',
                  background: '#fef9e7',
                  borderRadius: '8px',
                  border: '1px solid #fef3c7'
                }}>
                  <Text style={{ fontSize: '12px', color: '#92400e', lineHeight: '1.5' }}>
                    By creating an account, you agree to our{' '}
                    <Link to="/terms" style={{ color: '#667eea', textDecoration: 'none' }}>
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy" style={{ color: '#667eea', textDecoration: 'none' }}>
                      Privacy Policy
                    </Link>
                  </Text>
                </div>
              </Form>

              {/* Login Link */}
              <div style={{ 
                textAlign: 'center', 
                paddingTop: '20px', 
                borderTop: '1px solid #e5e7eb' 
              }}>
                <Text style={{ color: '#6b7280' }}>
                  Already have an account?{' '}
                  <Link 
                    to="/login"
                    style={{
                      color: '#667eea',
                      fontWeight: '600',
                      textDecoration: 'none'
                    }}
                  >
                    Sign in here
                  </Link>
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @media (max-width: 768px) {
          .container {
            padding-top: 100px !important;
            padding-bottom: 60px !important;
          }
        }
      `}</style>
    </div>
  );
}